import { m } from "@mojo/i18n";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import {
	IssueDetailModal,
	IssueFilters,
	IssueRow,
	type StatusFilter,
} from "@/components/issues";
import { PageHeader } from "@/components/layout";
import { Badge, Button } from "@/components/ui";
import {
	getIssues,
	getMilestones,
	type IssuePriority,
	type IssueType,
	type IssueWithDetails,
} from "@/data/issues";

export const Route = createFileRoute("/_authenticated/issues/")({
	loader: async () => {
		const [issuesResult, milestones] = await Promise.all([
			getIssues(),
			getMilestones(),
		]);
		return {
			issues: issuesResult.issues,
			total: issuesResult.total,
			openCount: issuesResult.openCount,
			actors: issuesResult.actors,
			milestones,
		};
	},
	component: IssuesPage,
});

// Power BI brand icon
function PowerBIIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="currentColor"
			className={className}
			aria-hidden="true"
		>
			<path d="M10 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2zm5-6v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2zm5-4v18a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2z" />
		</svg>
	);
}

// Generate and download Power BI Data Source (.pbids) file
function downloadPowerBIDataSource() {
	const pbidsContent = {
		version: "0.1",
		connections: [
			{
				details: {
					protocol: "postgresql",
					address: {
						server: "localhost:5432",
						database: "mojo",
					},
				},
				options: {
					commandTimeout: "300",
				},
				mode: "DirectQuery",
			},
		],
	};

	const blob = new Blob([JSON.stringify(pbidsContent, null, 2)], {
		type: "application/json",
	});
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = "mojo-issues.pbids";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

function IssuesPage() {
	const { issues, total, openCount, actors, milestones } =
		Route.useLoaderData();

	// Filter state
	const [selectedType, setSelectedType] = useState<IssueType | "all">("all");
	const [selectedStatus, setSelectedStatus] = useState<StatusFilter>("all");
	const [selectedPriority, setSelectedPriority] = useState<
		IssuePriority | "all"
	>("all");
	const [selectedMilestone, setSelectedMilestone] = useState<string>("all");
	const [selectedActor, setSelectedActor] = useState<string>("all");

	// Modal state
	const [selectedIssue, setSelectedIssue] = useState<IssueWithDetails | null>(
		null,
	);

	// Filter issues client-side
	const filteredIssues = useMemo(() => {
		return issues.filter((issue: IssueWithDetails) => {
			// Type filter
			if (selectedType !== "all" && issue.type !== selectedType) {
				return false;
			}

			// Status filter
			if (selectedStatus !== "all") {
				const stateTypeMap: Record<StatusFilter, string[]> = {
					all: [],
					backlog: ["backlog"],
					todo: ["unstarted"],
					in_progress: ["started"],
					in_review: ["started"], // Review is also started state type
					done: ["completed"],
					canceled: ["canceled"],
				};
				const allowedTypes = stateTypeMap[selectedStatus];
				if (!issue.state || !allowedTypes.includes(issue.state.type)) {
					return false;
				}
			}

			// Priority filter
			if (
				selectedPriority !== "all" &&
				issue.priorityLabel !== selectedPriority
			) {
				return false;
			}

			// Milestone filter
			if (
				selectedMilestone !== "all" &&
				issue.milestone?.id !== selectedMilestone
			) {
				return false;
			}

			// Actor filter
			if (selectedActor !== "all" && issue.actor?.id !== selectedActor) {
				return false;
			}

			return true;
		});
	}, [
		issues,
		selectedType,
		selectedStatus,
		selectedPriority,
		selectedMilestone,
		selectedActor,
	]);

	const clearFilters = () => {
		setSelectedType("all");
		setSelectedStatus("all");
		setSelectedPriority("all");
		setSelectedMilestone("all");
		setSelectedActor("all");
	};

	return (
		<div>
			<PageHeader
				title={m.issues_title()}
				description={m.issues_description()}
				badge={
					<Badge variant="default">
						{m.issues_open_count({ count: String(openCount) })}
					</Badge>
				}
				actions={
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={downloadPowerBIDataSource}
							className="inline-flex items-center gap-2 rounded-md border border-[#F2C811] bg-[#F2C811] px-6 py-2 font-semibold text-black text-sm shadow-md transition-all hover:border-[#E0B400] hover:bg-[#E0B400]"
						>
							<PowerBIIcon className="h-4 w-4" />
							Add to Power BI
						</button>
						<Link to="/issues/new">
							<Button icon={<Plus className="h-4 w-4" />} className="px-6">
								{m.issues_new_issue()}
							</Button>
						</Link>
					</div>
				}
			/>

			{/* Filters */}
			<div className="mb-4">
				<IssueFilters
					selectedType={selectedType}
					selectedStatus={selectedStatus}
					selectedPriority={selectedPriority}
					selectedMilestone={selectedMilestone}
					selectedActor={selectedActor}
					milestones={milestones}
					actors={actors}
					onTypeChange={setSelectedType}
					onStatusChange={setSelectedStatus}
					onPriorityChange={setSelectedPriority}
					onMilestoneChange={setSelectedMilestone}
					onActorChange={setSelectedActor}
					onClear={clearFilters}
				/>
			</div>

			{/* Issues List */}
			<div className="overflow-hidden rounded-xl border border-border bg-bg-elevated">
				{/* Table Header */}
				<div className="flex items-center gap-3 border-border border-b bg-bg-secondary/50 px-3 py-2 font-mono text-text-tertiary text-xs uppercase tracking-wider">
					<div className="w-2.5 shrink-0" />
					<span className="w-20 shrink-0">{m.issues_table_id()}</span>
					<span className="w-4 shrink-0" />
					<span className="flex-1">{m.issues_table_title()}</span>
					<span className="hidden w-20 shrink-0 md:block">
						{m.issues_table_actor()}
					</span>
					<span className="hidden w-24 shrink-0 lg:block">
						{m.issues_table_milestone()}
					</span>
					<span className="w-12 shrink-0">{m.issues_table_priority()}</span>
					<span className="w-6 shrink-0" />
					<span className="w-16 shrink-0 text-right">
						{m.issues_table_updated()}
					</span>
				</div>

				{/* Issues */}
				{filteredIssues.length > 0 ? (
					<div>
						{filteredIssues.map((issue) => (
							<IssueRow
								key={issue.id}
								issue={issue}
								onClick={() => setSelectedIssue(issue)}
							/>
						))}
					</div>
				) : (
					<div className="p-12 text-center">
						<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-bg-hover">
							<Search className="h-6 w-6 text-text-tertiary" />
						</div>
						<h3 className="mb-1 font-semibold text-lg text-text-primary">
							{m.issues_no_results()}
						</h3>
						<p className="mb-4 text-sm text-text-secondary">
							{m.issues_no_results_hint()}
						</p>
						<Link to="/issues/new">
							<Button variant="secondary">{m.issues_create_issue()}</Button>
						</Link>
					</div>
				)}

				{/* Footer */}
				{filteredIssues.length > 0 && (
					<div className="flex items-center justify-between border-border border-t bg-bg-secondary/30 px-3 py-2 text-text-tertiary text-xs">
						<span>
							{m.issues_showing_count({
								count: String(filteredIssues.length),
								total: String(total),
							})}
						</span>
						<span className="font-mono">
							{m.issues_open_count({ count: String(openCount) })}
						</span>
					</div>
				)}
			</div>

			{/* Issue Detail Modal */}
			<IssueDetailModal
				issue={selectedIssue}
				onClose={() => setSelectedIssue(null)}
			/>
		</div>
	);
}
